/**
 * DEV IN THE HOOD — entry point.
 *
 * The server writes the page (ADR-0003): the chrome, every section, its
 * text. The script keeps the motion. Boot order:
 *   1. what is only the script's: the veil, right away;
 *   2. the scenes (computed drawings), the shop cat and the chapter rail;
 *   3. the motion — then the loader leaves and the opening arrives.
 *
 * Built by Webpack Encore (entry "site", webpack.config.js), with the
 * stylesheet it imports below.
 *
 * @module main
 */

import '../scss/main.scss';
import { animateHeader, watchHeaderBackground } from './chrome/header.js';
import { playLoader } from './chrome/loader.js';
import { animateMenu } from './chrome/menu.js';
import { animateCursor, animateMagnetic } from './chrome/cursor.js';
import { enablePageTransitions, renderPageVeil } from './chrome/page-veil.js';
import { animatePaperGrain } from './chrome/paper-grain.js';
import { animateRail, renderRail } from './chrome/rail.js';
import { printSignature } from './chrome/signature.js';
import { placeShopCat } from './components/shop.js';
import { createSplitIntro } from './components/split.js';
import { ScrollTrigger } from './lib/gsap.js';
import { pageEntry } from './lib/page-data.js';
import { drawScenes } from './lib/scenes.js';
import { createSmoother, jumpToHash } from './lib/scroll.js';
import { animateCounters } from './motion/counters.js';
import { animateDepth } from './motion/depth.js';
import { refreshOnMediaLoad } from './motion/media-refresh.js';
import { animateReveals } from './motion/reveals.js';
import { animateStrips } from './motion/strips.js';
import { animateTerminals } from './motion/terminals.js';
import { animateText, hideTextUntilSplit, unhideStuckText } from './motion/text.js';
import * as contact from './pages/contact.js';
import * as home from './pages/home.js';
import * as notfound from './pages/notfound.js';
import * as project from './pages/project.js';
import * as work from './pages/work.js';

const PAGES = { home, work, project, contact, notfound };

/**
 * If the motion fails, the loader would sit on top of the page forever:
 * show the content as it is instead.
 */
function showWithoutMotion() {
    document.body.classList.remove('is-loading');
    document.querySelector('[data-loader]')?.style.setProperty('display', 'none');
    document.documentElement.classList.replace('js', 'no-js');
}

/**
 * @param {{ animate: () => void }} page
 */
function startMotion(page) {
    hideTextUntilSplit();
    createSmoother();

    animateHeader();
    watchHeaderBackground();
    animateMenu();
    animateCursor();
    animateMagnetic();

    // The page's own sections first: the pinned opening of the story must
    // exist before any trigger below it is measured.
    page.animate();

    animateReveals();
    animateStrips();
    animateDepth();
    animateRail();
    refreshOnMediaLoad();
    animateCounters();
    animateTerminals();
    animatePaperGrain();
    enablePageTransitions();

    // Built before the loader runs, so the halves start off screen.
    const intro = createSplitIntro();

    playLoader({
        onUnlock: jumpToHash,
        onReveal: () => intro?.play(),
        onDone: () => ScrollTrigger.refresh(),
    });

    // SplitText needs the final font metrics.
    document.fonts.ready.then(() => {
        animateText();
        ScrollTrigger.refresh();
    });

    unhideStuckText(3000);
    window.addEventListener('load', () => ScrollTrigger.refresh());
}

function boot() {
    printSignature();
    renderPageVeil();

    const page = PAGES[document.body.dataset.page];

    if (!page) {
        showWithoutMotion();
        return;
    }

    drawScenes();
    placeShopCat(pageEntry('components/shop').total ?? 0);
    renderRail(page.rail?.() ?? {});

    try {
        startMotion(page);
    } catch (error) {
        console.error('[motion]', error);
        showWithoutMotion();
    }
}

boot();
